import { isCNPJ, isCPF } from "brazilian-values";
import * as yup from "yup";

import { instance as signupStore } from "./store";

function duplicatedBrokerDocuments(document: string) {
  const broker = signupStore.getState().values.broker;
  const documents = [broker.document, broker.responsible.cpf];
  return !documents.includes(document);
}

function duplicatedBrokerEmail(email: string) {
  const broker = signupStore.getState().values.broker;
  const emails = [broker.email, broker.responsible.email];
  return !emails.includes(email);
}

export const commons = {
  document: yup
    .string()
    .required("Campo CPF/CNPJ é obrigatório.")
    .test(
      "document-test",
      "Por favor insira um CPF/CNPJ válido.",
      (value = "") => {
        if (value.length <= 14) return isCPF(value);
        return isCNPJ(value);
      }
    ),
  cpf: yup
    .string()
    .required("Campo CPF é obrigatório.")
    .test("cpf-test", "Por favor insira um CPF válido.", (value = "") =>
      isCPF(value)
    ),
  name: yup.string().required("Campo obrigatório."),
  phone: yup.string().required("Campo telefone/celular é obrigatório."),
  email: yup
    .string()
    .email("Por favor insira um e-mail válido.")
    .required("Campo e-mail é obrigatório."),
  address: yup.object().shape({
    zipcode: yup
      .string()
      .min(9, "Por favor insira um CEP válido.")
      .required("Campo CEP é obrigatório."),
    street: yup.string().required("Por favor confirme a rua."),
    number: yup.string().required("Campo número é obrigatório."),
    complement: yup.string(),
    neighborhood: yup.string().required("Por favor confirme o bairro."),
    city: yup.string().required("Por favor confirme a cidade."),
    state: yup.string().required("Por favor confirme o estado."),
  }),
};

export const brokerSchema = yup.object().shape({
  document: commons.document,
  name: yup.string().required("Campo razão social é obrigatório."),
  susep: yup
    .string()
    .required("Campo SUSEP é obrigatório.")
    .matches(/^[A-Z0-9]{5,5}[fjFJ]$/g, {
      message: "Por favor insira uma SUSEP válida.",
    }),
  email: commons.email,
  responsible: yup.object().shape({
    name: commons.name,
    email: commons.email,
    cpf: commons.cpf,
    phone: commons.phone,
  }),
});

export const responsibleSchema = yup.object().shape({
  cpf: commons.cpf.test(
    "duplicate-document",
    "Por favor insira um CPF/CNPJ diferente do corretor.",
    (document = "") => duplicatedBrokerDocuments(document)
  ),
  name: commons.name,
  email: commons.email.test(
    "duplicate-email",
    "Por favor insira um e-mail diferente do corretor.",
    (email = "") => duplicatedBrokerEmail(email)
  ),
  address: commons.address,
  foreigner: yup.object().shape({
    enabled: yup.boolean(),
    country: yup.string().when("enabled", {
      is: true,
      then: yup
        .string()
        .notOneOf([""], "Campo País de residência é obrigatório.")
        .required("Campo País de residência é obrigatório."),
    }),
  }),
  pep: yup.object().shape({
    enabled: yup.boolean(),
    relation: yup.object().shape({
      enabled: yup.boolean(),
      degree: yup.string().when("enabled", {
        is: true,
        then: yup
          .string()
          .notOneOf([""], "Campo grau de relacionamento é obrigatório.")
          .required("Campo grau de relacionamento é obrigatório."),
      }),
    }),
  }),
});

export const schemaByPartnerType = {
  individual: yup.object().shape({
    birthdate: yup.string().required("Campo data de nascimento é obrigatório."),
    email: commons.email.test(
      "duplicate-email",
      "Por favor insira um e-mail diferente do corretor.",
      (email = "") => duplicatedBrokerEmail(email)
    ),
    occupation: yup
      .string()
      .notOneOf([""], "Campo profissão é obrigatório.")
      .required("Campo profissão é obrigatório."),
    foreigner: yup.object().shape({
      enabled: yup.boolean(),
      country: yup.string().when("enabled", {
        is: true,
        then: yup
          .string()
          .notOneOf([""], "Campo país de residência é obrigatório.")
          .required("Campo país de residência é obrigatório."),
      }),
    }),
    pep: yup.object().shape({
      enabled: yup.boolean(),
      relation: yup.object().shape({
        enabled: yup.boolean(),
        degree: yup.string().when("enabled", {
          is: true,
          then: yup
            .string()
            .notOneOf([""], "Campo grau de relacionamento é obrigatório.")
            .required("Campo grau de relacionamento é obrigatório."),
        }),
      }),
    }),
  }),
  legal_entity: yup.object().shape({
    foundation_date: yup
      .string()
      .required("Campo data de fundação é obrigatório."),
    type: yup
      .string()
      .notOneOf([""], "Campo tipo de empresa é obrigatório.")
      .required("Campo tipo de empresa é obrigatório."),
    activity: yup.string().required("Campo atividade CNAE é obrigatório."),
    municipal_inscription: yup.string().required("Este campo é obrigatório."),
    simples_nacional: yup
      .string()
      .oneOf(
        ["SIM", "NAO"],
        "Campo optante pelo Simples Nacional é obrigatório."
      )
      .required("Campo optante pelo Simples Nacional é obrigatório."),
    partners: yup
      .array()
      .of(
        yup.object().shape({
          document: commons.document,
          name: commons.name,
          percentage: yup
            .string()
            .required("Campo porcentagem de participação é obrigatório.")
            .test("percentage-max", "Máximo de 99%", (value = "") => {
              return Number(value.replace(/\D/g, "")) <= 99;
            }),
        })
      )
      .notRequired(),
    responsible: responsibleSchema,
  }),
};

export const partnerSchema = yup.object().shape({
  document: commons.document.test(
    "duplicate-document",
    "Por favor insira um CPF/CNPJ diferente do corretor.",
    (document = "") => duplicatedBrokerDocuments(document)
  ),
  name: commons.name,
  phone: commons.phone,
  incomes: yup.string().notRequired(),
  net_equity: yup.string().notRequired(),
  address: commons.address,
});

export const bankSchema = yup.object().shape({
  name: yup
    .string()
    .notOneOf([""], "Campo banco é obrigatório.")
    .required("Campo banco é obrigatório."),
  branch: yup.string().required("Campo agência é obrigatório."),
  account: yup.string().required("Campo número da conta é obrigatório."),
  digit: yup.string().required("Campo dígito da conta é obrigatório."),
  email: commons.email,
});

export const companyPartner = yup.object().shape({
  document: commons.document.test(
    "duplicate-document",
    "Por favor insira um CPF/CNPJ diferente do corretor.",
    (document = "") => duplicatedBrokerDocuments(document)
  ),
  name: yup.string().required("Campo nome/razão social é obrigatório."),
  percentage: yup
    .string()
    .required("Campo porcentagem de participação é obrigatório.")
    .test("percentage-max", "Máximo de 99%", (value = "") => {
      return Number(value.replace(/\D/g, "")) <= 99;
    }),
});
